import styles from "./sidenav.module.css";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PhoneIcon from "@mui/icons-material/Phone";
import { useAuth } from "../hooks/AuthProvider";
import React, { useMemo, useState, useRef, useEffect, useCallback, useContext } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useMainMenu } from "../lib/navData";
import { SideNavDividerIcon } from "../svg/UtilsIcons";
import { LanguageContext } from "../context/LanguageContext";

import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import ClickAwayListener from "@mui/material/ClickAwayListener";

const NAV_PERMISSIONS = {
  dashboards: "view_dashboards",
  devices: ["view_devices", "devices_map"],
  usershealth: "view_users_health",
  myhealth: "view_my_health_data",
  usershealthdashboard: "view_health_dashboard",
  medicaldevices: "view-medical-devices",
  assetcards: "view_assetcards",
  notifications: "view_notifications",
  automations: "view_automations",
  locations: "view_locations",

  organizations: "view_organizations",
  users: "view_users",
  roles: "view_roles",
  permissions: "view_permissions",
  subscriptions: "view_subscriptions",
  plans: "view_plans",
  deviceplans: "view_devices",
  systemnotifications: "view_devices",
  auditlogs: "view_audit_logs",
  payment: "view_payment",

  templates: "view_templates",
  templateexamples: "add_template_examples",
  dashboardexamples: "add_dashboard_examples",
  ota: "view_ota",
  systemconfigurations: true,
  contact: true,
};

function hasPermissionForKey({ key, permissions, user }) {
  if (
    user?.role === "User" &&
    key !== "devices" &&
    key !== "dashboards" &&
    key !== "userhealth" &&
    key !== "myhealth" &&
    key !== "contact"
  ) return false;
  if (key === "subscriptions" && user?.role !== "Super") return false;
  if (key === "deviceplans" && user?.role !== "Super") return false;
  if (key === "systemnotifications" && user?.role !== "Super") return false;
  if (key === "systemconfigurations" && user?.role !== "Super") return false;

  const permKey = NAV_PERMISSIONS[key];
  if (!permKey) return true;
  if (permKey === true) return true;

  if (Array.isArray(permKey)) {
    return permKey.some((key) => Boolean(permissions?.[key]));
  }

  return Boolean(permissions?.[permKey]);
}

const SubMenu = ({ item, open, openKey, setOpenKey, onNavigate, isRtl }) => {
  const { permissions, user } = useAuth();
  const location = useLocation();
  const anchorRef = useRef(null);

  const filteredSubNav = useMemo(() => {
    const sub = Array.isArray(item.subNav) ? item.subNav : [];
    return sub.filter((s) => hasPermissionForKey({ key: s.key, permissions, user }));
  }, [item.subNav, permissions, user]);

  const hasSub = filteredSubNav.length > 0;
  const isOpen = openKey === item.key;
  const effectivePathname = useMemo(() => {
    const source = new URLSearchParams(location.search).get("from");
    if (location.pathname.startsWith("/users-health/details") && source === "my-health") {
      return "/my-health/details";
    }
    return location.pathname;
  }, [location.pathname, location.search]);
  const isPathActive = useCallback(
    (path) => effectivePathname === path || effectivePathname.startsWith(`${path}/`),
    [effectivePathname]
  );

  const isGroupBySubVisibility =
    item.key === "administration" || item.key === "configuration" || item.key === "userhealth";

  const isAllowedMain = isGroupBySubVisibility
    ? hasSub
    : hasPermissionForKey({ key: item.key, permissions, user });

  const closeMenu = useCallback(() => setOpenKey(null), [setOpenKey]);

  useEffect(() => {
    closeMenu();
  }, [closeMenu, location.pathname]);

  const hasActiveSub =
    hasSub && filteredSubNav.some((s) => isPathActive(s.path));

  const onMainClick = (e) => {
    if (!hasSub) {
      closeMenu();
      onNavigate?.();
      return;
    }

    e.preventDefault();
    setOpenKey((prev) => (prev === item.key ? null : item.key));
  };

  if (!isAllowedMain) return null;

  return (
    <div className={styles.menuItemWrap}>
      <NavLink
        ref={anchorRef}
        to={item.path}
        onClick={onMainClick}
        className={({ isActive }) =>
          `${styles.sidebarLink} ${isActive || isOpen || hasActiveSub ? styles.sidebarLinkActive : ""
          }`
        }
      >
        <div className={styles.mainItemLeft}>
          {item.icon}

          {open && (
            <span
              className={`${styles.sidebarLabel} ${open ? styles.sidebarLabelOpen : styles.sidebarLabelClosed
                }`}
            >
              {item.title}
            </span>
          )}
        </div>

        {open && hasSub && (
          isOpen ? (
            <ExpandLessIcon className={styles.expandIcon} />
          ) : (
            <ExpandMoreIcon className={styles.expandIcon} />
          )
        )}
      </NavLink>

      {isOpen && open && hasSub && (
        <div className={styles.subLinksWrap}>
          {filteredSubNav.map((sub) => (
            <NavLink
              key={sub.key}
              to={sub.path}
              className={({ isActive }) =>
                `${styles.subLink} ${isActive || isPathActive(sub.path) ? styles.subLinkActive : ""}`
              }
              onClick={() => {
                setOpenKey(item.key);
                onNavigate?.();
              }}
            >
              <div className={styles.subItemInner}>
                {sub.icon}
                <span className={styles.sidebarLabel}>{sub.title}</span>
              </div>
            </NavLink>
          ))}
        </div>
      )}

      {!open && hasSub && (
        <Popper
          open={isOpen}
          anchorEl={anchorRef.current}
          placement={isRtl ? "left-start" : "right-start"}
          disablePortal={false}
          modifiers={[
            { name: "offset", options: { offset: [10, 0] } },
            { name: "preventOverflow", options: { padding: 8 } },
          ]}
          style={{ zIndex: 999999 }}
        >
          <ClickAwayListener onClickAway={closeMenu}>
            <Paper className={styles.flyoutPaper} elevation={0} dir={isRtl ? "rtl" : "ltr"}>
              <div className={styles.flyoutTitle}>{item.title}</div>

              {filteredSubNav.map((sub) => (
                <NavLink
                  key={sub.key}
                  to={sub.path}
                  className={({ isActive }) =>
                    `${styles.flyoutLink} ${isActive || isPathActive(sub.path) ? styles.flyoutLinkActive : ""}`
                  }
                  onClick={() => {
                    closeMenu();
                    onNavigate?.();
                  }}
                >
                  <div className={styles.flyoutItemInner}>
                    {sub.icon}
                    <span className={styles.flyoutLabel}>{sub.title}</span>
                  </div>
                </NavLink>
              ))}
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </div>
  );
};

export default function Sidenav({ handleOpen }) {
  const [open, setOpen] = useState(true);
  const [openKey, setOpenKey] = useState(null);
  const { lang } = useContext(LanguageContext);
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => (typeof window !== "undefined" ? window.innerWidth <= 768 : false)
  );
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const menu = useMainMenu();
  const location = useLocation();
  const isRtl = ["ar", "ur"].includes(lang);
  const menuButtonOffset = open ? 235 : 50;
  const primaryMenu = useMemo(() => menu.filter((item) => item.key !== "contact"), [menu]);
  const contactItem = useMemo(() => menu.find((item) => item.key === "contact"), [menu]);

  const applyOpenState = useCallback((nextOpen) => {
    setOpen((prev) => {
      if (prev === nextOpen) return prev;
      handleOpen(nextOpen);
      return nextOpen;
    });
    if (!nextOpen) {
      setOpenKey(null);
    }
  }, [handleOpen]);

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      handleOpen(next);
      if (!next) {
        setOpenKey(null);
      }
      return next;
    });
  };

  const closeMobileDrawer = useCallback(() => {
    setIsMobileDrawerOpen(false);
    setOpenKey(null);
  }, []);

  useEffect(() => {
    const onSetOpen = (event) => {
      const requestedOpen = event?.detail?.open;
      if (typeof requestedOpen !== "boolean") return;
      applyOpenState(requestedOpen);
    };

    window.addEventListener("sama:sidenav:set-open", onSetOpen);
    return () => window.removeEventListener("sama:sidenav:set-open", onSetOpen);
  }, [applyOpenState]);

  useEffect(() => {
    const updateViewport = () => {
      const nextIsMobile = window.innerWidth <= 768;
      setIsMobileViewport(nextIsMobile);
      if (!nextIsMobile) {
        setIsMobileDrawerOpen(false);
      }
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    closeMobileDrawer();
  }, [closeMobileDrawer, location.pathname]);

  useEffect(() => {
    const onToggleMobileDrawer = () => {
      if (!isMobileViewport) return;
      setIsMobileDrawerOpen((prev) => !prev);
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeMobileDrawer();
      }
    };

    window.addEventListener("sama:sidenav:toggle-mobile-drawer", onToggleMobileDrawer);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("sama:sidenav:toggle-mobile-drawer", onToggleMobileDrawer);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [closeMobileDrawer, isMobileViewport]);

  const isExpanded = isMobileViewport ? true : open;
  const navClassName = [
    isExpanded ? styles.sidenavOpened : styles.sidenavClosed,
    isMobileViewport ? styles.sidenavMobile : "",
    isMobileViewport && isMobileDrawerOpen ? styles.sidenavMobileOpen : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {isMobileViewport && isMobileDrawerOpen ? (
        <button
          type="button"
          className={styles.mobileOverlay}
          onClick={closeMobileDrawer}
          aria-label="Close navigation"
        />
      ) : null}

      <div
        className={navClassName}
        data-sidenav-open={isExpanded ? "true" : "false"}
        data-sidenav-mobile={isMobileViewport ? "true" : "false"}
        data-sidenav-rtl={isRtl ? "true" : "false"}
      >
        <div className="logoHeader">
          <div className="logoRow">
            <div className="logoMark" aria-hidden="true">
              <div className="logoBar logoBarWhite" style={{ height: 15.194 }} />
              <div className="logoBar logoBarWhite" style={{ height: 23.542 }} />
              <div className="logoBar logoBarCyan" style={{ height: 34.294 }} />
              <div className="logoBar logoBarCyan" style={{ height: 29.088 }} />
              <div className="logoBar logoBarCyan" style={{ height: 17.602 }} />
              <div className="logoBar logoBarCyan" style={{ height: 11.253 }} />
              <div className="logoBar logoBarCyan" style={{ height: 5.721 }} />
              <div className="logoBar logoBarCyan" style={{ height: 3.269 }} />
            </div>

            {isExpanded && <span className="logoTitle">Sama-IOT</span>}
          </div>

          <div className="logoDividerWrap" aria-hidden="true">
            <SideNavDividerIcon className="logoDividerIcon" />
          </div>
        </div>

        <div className={styles.menuScroll}>
          {primaryMenu.map((item) => (
            <SubMenu
              key={item.key}
              item={item}
              open={isExpanded}
              openKey={openKey}
              setOpenKey={setOpenKey}
              onNavigate={isMobileViewport ? closeMobileDrawer : undefined}
              isRtl={isRtl}
            />
          ))}
        </div>

      {contactItem ? (
        <div className={styles.bottomNav}>
          <div className={styles.bottomDivider} aria-hidden="true">
            <SideNavDividerIcon className={styles.bottomDividerIcon} />
          </div>
          <SubMenu
            item={{
              ...contactItem,
              icon: (
                <PhoneIcon
                  style={{
                    width: "24px",
                    height: "24px",
                    color: "var(--sidenav-icons)",
                  }}
                />
              ),
            }}
            open={isExpanded}
            openKey={openKey}
            setOpenKey={setOpenKey}
            onNavigate={isMobileViewport ? closeMobileDrawer : undefined}
            isRtl={isRtl}
          />
        </div>
      ) : null}

      {!isMobileViewport ? (
        <button
          className={styles.menuBtn}
          onClick={toggleOpen}
          style={{ insetInlineStart: menuButtonOffset }}
        >
          {open ? (
            isRtl ? <KeyboardDoubleArrowRightIcon /> : <KeyboardDoubleArrowLeftIcon />
          ) : (
            isRtl ? <KeyboardDoubleArrowLeftIcon /> : <KeyboardDoubleArrowRightIcon />
          )}
        </button>
      ) : null}
    </div>
    </>
  );
}
